import { useSyncExternalStore, type ReactNode } from 'react';
import {
  tShared, getSharedComponentLang, subscribeSharedComponentLang,
  type SharedI18nKey,
} from '../../../lib/shared-i18n';
import './Stepper.css';

/**
 * Stepper — 워크플로우/파이프라인 단계 진행 표시.
 *
 * Approval 결재선, Task project 단계, onboarding multi-step, 사용자 진행 등
 * generic 도입. 본 cycle 은 ApprovalDetail 의 결재선 채택 (1).
 *
 * status 의미:
 *   - pending  : 미진행 (아직 도달 안 함)
 *   - active   : 현재 단계 (강조)
 *   - done     : 완료
 *   - rejected : 반려/실패
 *   - skipped  : 건너뜀 (대리결재 등)
 *
 * orientation:
 *   - horizontal: step 들이 좌→우
 *   - vertical: 위→아래 (Approval 결재선 default)
 *
 * accessibility:
 *   - role="list" + 각 step role="listitem" + aria-current="step" (active)
 *   - status 는 aria-label 에도 반영 (예: "step 2: 승인됨")
 *
 * motion:
 *   - status 전환 시 color/opacity transition (--duration-base + --ease-standard)
 *   - active step 의 dot pulse 는 reduced-motion 시 자동 정지 (tokens global gate)
 */

export type StepStatus = 'pending' | 'active' | 'done' | 'rejected' | 'skipped';

export interface StepperItem {
  /** 고유 key. */
  id: string;
  /** 메인 라벨. */
  label: string;
  /** 보조 라벨 (시간, 코멘트, 담당자 이름 등). */
  sub?: ReactNode;
  /** 단계 상태. */
  status: StepStatus;
}

export interface StepperProps {
  steps: ReadonlyArray<StepperItem>;
  orientation?: 'horizontal' | 'vertical';
  /** 사용자 클릭으로 step 점프 가능 여부 (default false — 단순 표시). */
  onStepClick?: (id: string) => void;
  className?: string;
  'aria-label'?: string;
}

const STATUS_LABEL_KEYS: Record<StepStatus, SharedI18nKey> = {
  pending: 'shared.step.status.pending',
  active: 'shared.step.status.active',
  done: 'shared.step.status.done',
  rejected: 'shared.step.status.rejected',
  skipped: 'shared.step.status.skipped',
};

export function Stepper({
  steps,
  orientation = 'vertical',
  onStepClick,
  className,
  'aria-label': ariaLabel,
}: StepperProps) {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  const cn = ['wc-stepper', `wc-stepper--${orientation}`, className ?? ''].filter(Boolean).join(' ');
  return (
    <ol className={cn} role="list" aria-label={ariaLabel}>
      {steps.map((s, idx) => {
        const isActive = s.status === 'active';
        const stepCn = ['wc-stepper__item', `wc-stepper__item--${s.status}`].join(' ');
        const ariaLabelStep = tShared('shared.step.aria_step', {
          n: idx + 1,
          label: s.label,
          status: tShared(STATUS_LABEL_KEYS[s.status]),
        });
        return (
          <li
            key={s.id}
            role="listitem"
            className={stepCn}
            aria-current={isActive ? 'step' : undefined}
            aria-label={ariaLabelStep}
          >
            <button
              type="button"
              className="wc-stepper__indicator"
              onClick={onStepClick ? () => onStepClick(s.id) : undefined}
              disabled={!onStepClick}
              aria-hidden={!onStepClick}
              tabIndex={onStepClick ? 0 : -1}
            >
              <span className="wc-stepper__dot" aria-hidden>
                {s.status === 'done' && <DotCheck />}
                {s.status === 'rejected' && <DotX />}
                {s.status === 'skipped' && <span className="wc-stepper__dot-skip" />}
                {(s.status === 'active' || s.status === 'pending') && (
                  <span className="wc-stepper__dot-num">{idx + 1}</span>
                )}
              </span>
            </button>
            <div className="wc-stepper__content">
              <div className="wc-stepper__label">{s.label}</div>
              {s.sub && <div className="wc-stepper__sub">{s.sub}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function DotCheck() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden>
      <path
        d="M3.5 8.5l3 3 6-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DotX() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden>
      <path
        d="M4 4l8 8M12 4l-8 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
